import { startOfMonth, endOfMonth, startOfWeek, endOfWeek, addDays, isSameMonth, isToday, format } from "date-fns";
import { Lock } from "lucide-react";
import { iso } from "@/lib/format";
import { isOffDay, isPiketDay } from "@/lib/period";

const WEEK = ["Sen", "Sel", "Rab", "Kam", "Jum", "Sab", "Min"];

const cellTone = (s, sets, off, piket) => {
  if (sets.actual.has(s)) return "bg-rose-400 text-white border-rose-400";
  if (sets.predicted.has(s)) return "bg-rose-50 text-rose-500 border-rose-300 border-dashed";
  if (off) return "bg-sky-100 text-sky-700 border-sky-100";
  if (piket) return "bg-amber-100 text-amber-700 border-amber-200";
  return "bg-white text-gray-700 border-pink-50";
};

export const MonthCalendar = ({ month, sets, dayMap, prefs, events, holidays = {}, pickMode, onPick }) => {
  const start = startOfWeek(startOfMonth(month), { weekStartsOn: 1 });
  const end = endOfWeek(endOfMonth(month), { weekStartsOn: 1 });
  const cells = [];
  for (let d = start; d <= end; d = addDays(d, 1)) cells.push(d);

  return (
    <div data-testid="mood-calendar">
      <div className="grid grid-cols-7 gap-1.5 mb-1.5">
        {WEEK.map((w) => <span key={w} className="text-center text-[11px] font-semibold text-pink-400">{w}</span>)}
      </div>
      <div className="grid grid-cols-7 gap-1.5">
        {cells.map((d) => {
          const s = iso(d);
          const inMonth = isSameMonth(d, month);
          const off = isOffDay(s, dayMap);
          const piket = !off && isPiketDay(s, dayMap, prefs);
          const isPeriod = sets.actual.has(s) || sets.predicted.has(s);
          const evs = events.filter((e) => e.date === s);
          const mood = dayMap[s]?.mood;
          const hol = holidays[s];
          const label = hol?.name || evs[0]?.title;
          return (
            <button
              key={s}
              onClick={() => onPick(s)}
              data-testid={`calendar-day-${s}`}
              title={[hol?.name, ...evs.map((e) => e.title)].filter(Boolean).join(", ") || undefined}
              className={`relative aspect-square rounded-2xl border text-sm font-semibold flex flex-col items-center justify-center overflow-hidden transition-transform hover:scale-105 ${cellTone(s, sets, off, piket)} ${inMonth ? "" : "opacity-30"} ${isToday(d) ? "ring-2 ring-pink-500 ring-offset-1" : ""} ${pickMode && inMonth && !off && !piket ? "outline outline-2 outline-dashed outline-sky-300" : ""}`}
            >
              <span className={hol && !sets.actual.has(s) ? "text-red-500" : ""} data-testid={hol ? `calendar-holiday-${s}` : undefined}>{format(d, "d")}</span>
              {label && <span className={`hidden sm:block text-[9px] leading-tight font-medium truncate max-w-full px-1 ${hol ? "text-red-500" : "text-violet-500"}`} data-testid={`calendar-day-label-${s}`}>{label}</span>}
              {off && <Lock className="absolute bottom-1 right-1 w-3 h-3 text-sky-500" data-testid={`calendar-day-lock-${s}`} />}              {isPeriod && (off || piket) && <span className={`absolute top-0 inset-x-2 h-1 rounded-b ${off ? "bg-sky-400" : "bg-amber-400"}`} data-testid={`calendar-day-flag-${s}`} />}
              {sets.fertile.has(s) && !sets.actual.has(s) && <span className="absolute top-1 right-1 w-1.5 h-1.5 rounded-full bg-emerald-400" />}
              {mood && <span className="absolute top-1 left-1 w-1.5 h-1.5 rounded-full bg-amber-400" />}
              {evs.length > 0 && (
                <span className="absolute bottom-1 flex gap-0.5">
                  {evs.slice(0, 3).map((e) => <span key={e.id} className="w-1.5 h-1.5 rounded-full bg-violet-400" />)}
                </span>
              )}
            </button>
          );
        })}
      </div>
      <div className="flex flex-wrap gap-x-4 gap-y-1.5 mt-4 text-[11px] text-gray-500">
        <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded bg-rose-400" /> Mens</span>
        <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded border border-dashed border-rose-300 bg-rose-50" /> Prediksi mens</span>
        <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded bg-sky-100" /> Libur</span>
        <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded bg-amber-100 border border-amber-200" /> Piket</span>
        <span className="flex items-center gap-1.5"><span className="text-red-500 font-bold">17</span> Libur nasional</span>
        <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-emerald-400" /> Masa subur</span>
        <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-violet-400" /> Acara</span>
        <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-amber-400" /> Mood</span>
      </div>
    </div>
  );
};
